import {
  compact,
  difference,
  find,
  isEmpty,
  isEqual,
  isNil,
  map,
  size,
  toString
} from 'lodash/fp'
import React from 'react'
import { Box, type ButtonProps } from '@mui/material'
import { Chart, getElementAtEvent } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  ScatterController,
  PointElement,
  Title,
  Legend,
  type ChartConfiguration,
  Tooltip
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import { createBiChart, createCompareChart } from './functions'
import log from 'loglevel'
import { type Identifier, Loading, NotFound, type RaRecord } from 'react-admin'
import {
  useGetElectricityManyYearlyBySpin,
  useGetElectricityMonthlyAverageBySpin
} from 'components/DataProvider'
import {
  type ElectricityYearly,
  type ElectricityYearlyData
} from '../../../../cdk/bapp/lib/lambda-api/src/types'
import { format, parseISO } from 'date-fns/fp'
import { type ConfigProps } from 'components/Dashboard'

log.setLevel((process.env['LOG_LEVEL'] as log.LogLevelDesc) ?? log.levels.DEBUG)

ChartJS.register(
  CategoryScale,
  ScatterController,
  PointElement,
  Title,
  Legend,
  Tooltip,
  annotationPlugin
)

const defaultWidth = 800
const defaultHeight = 600

export const BIChart: React.FC<
  {
    selected: RaRecord[]
    date: string
    compare: any
    labels: Array<{ label: string; color: string }>
    width?: number
    height?: number
    onClickItem?: (data: { x: number; y: number; id: Identifier }) => void
    setVisible: (status: boolean) => void
  } & ButtonProps &
    ConfigProps
> = ({
  config,
  selected,
  date,
  compare,
  width,
  height,
  onClickItem,
  labels,
  setVisible
}) => {
  const [chart, setChart] = React.useState<ChartConfiguration | null>(null)
  const spins = map((item: RaRecord) => item['spin'])(selected)
  const [target, setTarget] = React.useState<{
    spins: string[]
    date: string
    compare: any
  } | null>(null)
  const { data: electricityData, isLoading } =
    useGetElectricityManyYearlyBySpin(
      format('yyyy-MM-dd')(parseISO(date)),
      spins
    )
  React.useEffect(() => {
    if (!(isLoading as boolean) && !isNil(electricityData)) {
      const { targetKey } = compare
      const data = compact(
        map((item: ElectricityYearlyData) => {
          const { data } = item
          if (isNil(data)) return undefined
          const { amounts: amountsRaw, spin } = data
          const amount = find(
            (item: { date: string; daily: number }) => item.date === date
          )(amountsRaw)
          const user = find((item: RaRecord) => item['spin'] === spin)(selected)
          return isNil(amount)
            ? undefined
            : {
                name: !isNil(user)
                  ? !isNil(user[targetKey])
                    ? toString(user[targetKey])
                    : user['name']
                  : '',
                id: !isNil(user) ? user.id : '',
                date,
                amount: amount.daily
              }
        })(electricityData)
      )
      const chart = createBiChart(config.biChart, date, data, compare, labels)
      const { spins: currentSpins } = target ?? { spins: [] }
      if (!isEmpty(difference(spins)(currentSpins))) {
        setTarget({ spins, date, compare })
      }
      setChart(chart)
      setVisible(true)
    } else {
      if (!isEqual(target)({ spins, date, compare })) {
        setChart(null)
      }
    }
  }, [compare, selected, electricityData, isLoading, labels])
  const chartRef = React.useRef()
  if (isLoading as boolean) return <Loading />
  if (isNil(chart)) return <NotFound />
  const { options, data } = chart
  if (isNil(options)) return <></>
  const onClickRaw = (event: React.MouseEvent<HTMLCanvasElement>): void => {
    if (isNil(chartRef.current)) return
    const data = getElementAtEvent(chartRef.current, event)
    if (isNil(data[0])) return
    // @ts-expect-error ts(2339)
    const { raw } = data[0].element.$context
    if (!isNil(onClickItem)) onClickItem(raw)
  }
  return (
    <Box sx={{ bgcolor: 'white' }}>
      <Chart
        type="scatter"
        width={width ?? defaultWidth}
        height={height ?? defaultHeight}
        options={options}
        data={data}
        id="bi-chart"
        ref={chartRef}
        onClick={onClickRaw}
      />
    </Box>
  )
}
export const CompareChart: React.FC<
  {
    date: string
    targets: string[]
    labels: string[]
    width?: number
    height?: number
  } & ConfigProps
> = ({ config, date, targets, labels, width, height }) => {
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: electricityData0, isLoading: isLoading0 } =
    useGetElectricityMonthlyAverageBySpin(date, targets[0] as string)
  const { data: electricityData1, isLoading: isLoading1 } =
    useGetElectricityMonthlyAverageBySpin(date, targets[1] as string)
  React.useEffect(() => {
    if (
      !(isLoading0 as boolean) &&
      !isNil(electricityData0) &&
      !(isLoading1 as boolean) &&
      !isNil(electricityData1)
    ) {
      if (isNil(electricityData0.data) || isNil(electricityData1.data)) return
      const amountsRaw = compact(
        map((item: ElectricityYearly) => {
          return find(
            (item: { date: string; halfHourly: number[] }) => item.date === date
          )(item.amounts)
        })([electricityData0.data, electricityData1.data])
      )
      if (size(amountsRaw) !== size(targets)) return
      const amounts = map((rawData: { date: string; halfHourly: number[] }) => {
        const { halfHourly } = rawData
        return halfHourly
      })(amountsRaw)
      const chart = createCompareChart(
        date,
        config.compareChart,
        amounts,
        labels
      )
      setChart(chart)
    }
  }, [targets, electricityData0, isLoading0, electricityData1, isLoading1])
  if ((isLoading0 as boolean) || (isLoading1 as boolean)) return <Loading />
  if (isNil(chart)) return <NotFound />
  const { type, options, data } = chart
  if (isNil(options)) return <></>
  return (
    <Box sx={{ bgcolor: 'white' }}>
      <Chart
        type={type}
        width={width ?? defaultWidth}
        height={height ?? defaultHeight}
        options={options}
        data={data}
        id="bi-compare-chart"
      />
    </Box>
  )
}
